import { Add, CancelOutlined, Search } from "@material-ui/icons";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";
import { useAsyncFn } from "react-use";
import styled from "styled-components";
import { APISearchParams } from "../../api/base";
import * as projectsAPI from "../../api/projects";
import { APIRCodeSearchResult } from "../../api/types/projects";
import Button from "../../components/Button";
import Loading from "../../components/Loading";
import { useStore } from "../../store";
import { isRCodePattern } from "../../utils";
import { Page } from "../Page";
import NewProject from "./NewProject";
import Projects from "./Projects";
import RCodeSearchResults from "./RCodeSearchResults";

const RootContainer = styled.div`
  margin: 1.875rem 3.75rem;
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid #b9b9b9;
  border-radius: 0.25rem;
  overflow: hidden;
  height: 38px;
  width: fit-content;

  &:focus-within {
    border-color: #4caf50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }
`;

const SearchInput = styled.input`
  padding: 0.4rem 0.75rem;
  border: none;
  font-size: 0.875rem;
  outline: none;
  min-width: 280px;
  background: #ffffff;
`;

const SearchActionButton = styled.button`
  background: transparent;
  border: none;
  border-left: 1px solid #b9b9b9;
  color: #666;
  padding: 0 0.6rem;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    background: #f5f5f5;
    color: #333;
  }
`;

const SearchSubmitButton = styled(SearchActionButton)`
  background: #4caf50;
  border-left: 1px solid #43a047;
  color: #ffffff;

  &:hover {
    background: #43a047;
    color: #ffffff;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const ProjectsDrive: React.FC = () => {
  // Keep R-Code card rendering bounded even when one material occurs in many projects.
  const rCodePageSize = 20;
  const store = useStore();
  const urlPrefix = `/projects/`;
  const [newProjectModalShow, setNewProjectModalShow] =
    useState<boolean>(false);

  const [inputValue, setInputValue] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [rCodeSearchResult, setRCodeSearchResult] = useState<APIRCodeSearchResult | null>(null);
  const [rCodeSearchError, setRCodeSearchError] = useState<string | null>(null);
  const [rCodePage, setRCodePage] = useState<number>(1);
  const rCodeRequestId = useRef(0);
  const rCodeAbortController = useRef<AbortController | null>(null);
  const isRCodeSearch = Boolean(searchQuery && isRCodePattern(searchQuery));

  // Debounce input changes to avoid issuing a request for every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      const nextQuery = inputValue.trim();
      setSearchQuery((currentQuery) =>
        currentQuery === nextQuery ? currentQuery : nextQuery
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setRCodePage(1);
      setSearchQuery(inputValue);
    }
  };

  const handleSearchSubmit = () => {
    setRCodePage(1);
    setSearchQuery(inputValue);
  };

  const handleClearSearch = () => {
    setInputValue("");
    setSearchQuery("");
    setRCodeSearchResult(null);
    setRCodeSearchError(null);
    setRCodePage(1);
  };

  // R-Code results stay page-bounded so ProjectCard rendering scales with large datasets.
  const [{ loading: rCodeLoading }, performRCodeSearch] = useAsyncFn(
    async (rcode: string, page = 1) => {
      rCodeAbortController.current?.abort();
      const controller = new AbortController();
      rCodeAbortController.current = controller;
      const requestId = ++rCodeRequestId.current;
      setRCodeSearchError(null);
      try {
        // Do not constrain is_active: R-Code results include archived and foldered projects.
        const result = await projectsAPI.searchByRCode(
          rcode,
          page,
          rCodePageSize,
          undefined,
          controller.signal
        );
        // Ignore responses from earlier queries or pages that finish after a newer request.
        if (requestId === rCodeRequestId.current && !controller.signal.aborted) {
          setRCodeSearchResult(result);
        }
      } catch (error: any) {
        if (requestId === rCodeRequestId.current && !controller.signal.aborted) {
          setRCodeSearchError(
            error?.response?.data?.error || "Failed to search R-Code"
          );
          setRCodeSearchResult(null);
        }
      }
    },
    []
  );

  // Keep project-name searches local; only R-Code-shaped input calls the separate endpoint.
  useEffect(() => {
    if (searchQuery && isRCodePattern(searchQuery)) {
      performRCodeSearch(searchQuery, rCodePage);
    } else if (searchQuery) {
      // Regular project name search - clear R-Code results
      rCodeAbortController.current?.abort();
      rCodeRequestId.current += 1;
      setRCodeSearchResult(null);
      setRCodeSearchError(null);
    } else {
      // No search query - clear everything
      rCodeAbortController.current?.abort();
      rCodeRequestId.current += 1;
      setRCodeSearchResult(null);
      setRCodeSearchError(null);
    }
  }, [searchQuery, rCodePage, performRCodeSearch]);

  useEffect(() => () => rCodeAbortController.current?.abort(), []);

  const fetchProjectFoldersList = async (params: APISearchParams) => {
    return store.projectFolders.listActive(1, -1, params);
  };

  const [
    { loading: projectFoldersLoading, value: projectFolders },
    fetchProjectFolders,
  ] = useAsyncFn(async () => {
    const params: APISearchParams = {
      is_active: true,
    };

    await fetchProjectFoldersList(params);
    return Array.from(store.projectFolders.listActiveItems.values());
  });

  const fetchProjectsList = async (params: APISearchParams) => {
    return store.projects.listActive(1, -1, params);
  };

  const [{ loading: projectsLoading, value: activeProject }, fetchProjects] =
    useAsyncFn(async () => {
      const params: APISearchParams = {
        is_active: true,
      };

      const { count } = await fetchProjectsList(params);
      return {
        count,
        results: Array.from(store.projects.listActiveItems.values()),
      };
    });

  const fetchArchivedProjectFoldersList = async (params: APISearchParams) => {
    return store.projectFolders.listArchived(1, -1, params);
  };

  const [
    { loading: archivedProjectFoldersLoading, value: archivedProjectFolders },
    fetchArchivedProjectFolders,
  ] = useAsyncFn(async () => {
    const params: APISearchParams = {
      is_active: false,
    };
    await fetchArchivedProjectFoldersList(params);
    return Array.from(store.projectFolders.listArchivedItems.values());
  });

  const fetchArchivedProjectsList = async (params: APISearchParams) => {
    return store.projects.listArchived(1, -1, params);
  };

  const [
    { loading: archivedProjectsLoading, value: archivedProjects },
    fetchArchivedProjects,
  ] = useAsyncFn(async () => {
    const params: APISearchParams = {
      is_active: false,
    };

    const { count } = await fetchArchivedProjectsList(params);
    return {
      count,
      results: Array.from(store.projects.listArchiveItems.values()),
    };
  });

  useEffect(() => {
    fetchProjectFolders();
    fetchProjects();
    fetchArchivedProjectFolders();
    fetchArchivedProjects();
  }, [
    fetchProjectFolders,
    fetchProjects,
    fetchArchivedProjectFolders,
    fetchArchivedProjects,
  ]);

  const handleFolderSubmit = async () => {
    await Promise.all([fetchProjectFolders(), fetchProjects()]);
  };

  const handleArchivedFolderSubmit = async () => {
    await Promise.all([fetchArchivedProjectFolders(), fetchArchivedProjects()]);
  };

  const handleProjectSubmit = async () => {
    await fetchProjects();
  };

  const handleArchivedProjectSubmit = async () => {
    await fetchArchivedProjects();
  };

  const handleArchiveRestore = async () => {
    await Promise.all([fetchProjects(), fetchArchivedProjects()]);
  };

  return (
    <Page
      title="Projects"
      breadcrumb={[
        {
          title: "Projects Drive",
          subtitle: "Projects",
          link: "/projects",
        },
      ]}
    >
      <RootContainer>
        <TopBar>
          <Button
            variant="green"
            size="page"
            onClick={() => setNewProjectModalShow(true)}
          >
            <Add />
            New Project
          </Button>
          <SearchWrapper>
            <SearchInput
              type="text"
              placeholder="Search by project name or R-Code"
              value={inputValue}
              onChange={(e) => {
                setRCodePage(1);
                setInputValue(e.target.value);
              }}
              onKeyDown={handleSearchKeyDown}
            />
            {inputValue && (
              <SearchActionButton type="button" onClick={handleClearSearch}>
                <CancelOutlined style={{ fontSize: "1rem" }} />
              </SearchActionButton>
            )}
            <SearchSubmitButton type="button" onClick={handleSearchSubmit}>
              <Search style={{ fontSize: "1rem" }} />
            </SearchSubmitButton>
          </SearchWrapper>
        </TopBar>

        {!isRCodeSearch &&
        projectsLoading &&
        projectFoldersLoading &&
        archivedProjectFoldersLoading &&
        archivedProjectsLoading ? (
          <Loading />
        ) : (
          <>
            {/* Show R-Code search results if applicable */}
            {isRCodeSearch && (
              <RCodeSearchResults
                rcode={searchQuery}
                result={rCodeSearchResult}
                loading={rCodeLoading}
                error={rCodeSearchError}
                urlPrefix={urlPrefix}
                page={rCodePage}
                pageSize={rCodePageSize}
                onPageChange={setRCodePage}
                onProjectSubmit={() => performRCodeSearch(searchQuery, rCodePage)}
                onArchiveRestore={() => performRCodeSearch(searchQuery, rCodePage)}
              />
            )}

            {/* Show project name search results or all projects if not searching */}
            <Projects
              title="Active Projects"
              urlPrefix={urlPrefix}
              projects={activeProject?.results}
              folders={projectFolders}
              projectCount={activeProject?.count}
              searchQuery={searchQuery}
              hideIfRCode={isRCodeSearch}
              onFolderSubmit={handleFolderSubmit}
              onProjectSubmit={handleProjectSubmit}
              onArchiveRestore={handleArchiveRestore}
            />
            <Projects
              archived
              title="Archived Projects"
              urlPrefix={urlPrefix}
              projects={archivedProjects?.results}
              projectCount={archivedProjects?.count}
              folders={archivedProjectFolders}
              searchQuery={searchQuery}
              hideIfRCode={isRCodeSearch}
              onFolderSubmit={handleArchivedFolderSubmit}
              onProjectSubmit={handleArchivedProjectSubmit}
              onArchiveRestore={handleArchiveRestore}
              noFilter
            />
          </>
        )}

        <NewProject
          isOpen={newProjectModalShow}
          setModal={setNewProjectModalShow}
        />
      </RootContainer>
    </Page>
  );
};

export default observer(ProjectsDrive);
